/**
 * GovContactsPage — Global gov contact rolodex.
 * CO/CS/COR/POC + teaming partners + subs + suppliers + bond/insurance agents
 * + past performance references. Linkable to any solicitation or construction job.
 * Route: /gov/contacts
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, CONTACT_TYPES } from './govShared';

interface Contact {
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  title?: string;
  contact_type: string;
  organization?: string;
  sub_organization?: string;
  duns_or_uei?: string;
  email?: string;
  email_secondary?: string;
  phone?: string;
  phone_mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  tags?: string[];
  small_business_certs?: string[];
  naics_codes?: string[];
  notes?: string;
  preferred_contact_method?: string;
  do_not_contact?: boolean;
  do_not_contact_reason?: string;
  created_at?: string;
  updated_at?: string;
}

interface LinkedSolicitation {
  role_on_solicitation?: string;
  is_primary?: boolean;
  solicitation: { id: string; title: string; solicitation_number?: string; pipeline: string };
}
interface LinkedJob {
  role_on_job?: string;
  is_primary?: boolean;
  construction_job: { id: string; contract_number?: string; bid_number?: string; solicitation?: { title?: string } };
}

const emptyContact = (): Partial<Contact> => ({
  full_name: '', contact_type: 'poc', tags: [], small_business_certs: [], naics_codes: [],
});

export default function GovContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState<Contact | null>(null);
  const [linkedSols, setLinkedSols] = useState<LinkedSolicitation[]>([]);
  const [linkedJobs, setLinkedJobs] = useState<LinkedJob[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<Partial<Contact>>(emptyContact());
  const [saving, setSaving] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (search) params.set('search', search);
      const { data } = await api.get(`/gov/contacts?${params.toString()}`);
      setContacts(data?.contacts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [typeFilter, search]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openContact = async (c: Contact) => {
    setSelected(c);
    try {
      const { data } = await api.get(`/gov/contacts/${c.id}`);
      setLinkedSols(data?.solicitations || []);
      setLinkedJobs(data?.construction_jobs || []);
    } catch { setLinkedSols([]); setLinkedJobs([]); }
  };

  const openNewModal = () => { setForm(emptyContact()); setShowModal(true); };
  const openEditModal = () => { if (selected) { setForm({ ...selected }); setShowModal(true); } };

  const saveContact = async () => {
    if (!form.full_name?.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const isEdit = !!(form as any).id;
      const { data } = isEdit
        ? await api.patch(`/gov/contacts/${(form as any).id}`, form)
        : await api.post('/gov/contacts', form);
      const saved = data?.contact;
      setShowModal(false);
      await fetchContacts();
      if (saved) setSelected(saved);
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const deleteContact = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete ${selected.full_name}? This removes all links to solicitations and jobs.`)) return;
    try {
      await api.delete(`/gov/contacts/${selected.id}`);
      setSelected(null);
      await fetchContacts();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Delete failed');
    }
  };

  const typeMeta = (t: string) => CONTACT_TYPES.find(ct => ct.key === t) || CONTACT_TYPES[CONTACT_TYPES.length - 1];

  const grouped = useMemo(() => {
    const by: Record<string, Contact[]> = {};
    contacts.forEach(c => { (by[c.contact_type] ||= []).push(c); });
    return CONTACT_TYPES.filter(ct => by[ct.key]?.length).map(ct => ({ meta: ct, items: by[ct.key] }));
  }, [contacts]);

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>👥 Contacts</h1>
          <p style={S.subtitle}>{contacts.length} contacts · government POCs, teaming partners, subs, suppliers</p>
        </div>
        <button onClick={openNewModal} style={S.addBtn}>+ New Contact</button>
      </div>

      <div style={S.toolbar}>
        <input type="text" placeholder="🔍 Search name, email, org, phone…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ ...S.input, minWidth: 320 }} />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={S.select}>
          <option value="all">All types ({contacts.length})</option>
          {CONTACT_TYPES.map(ct => <option key={ct.key} value={ct.key}>{ct.icon} {ct.label}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 400px' : '1fr', gap: 16 }}>
        <div>
          {loading ? (
            <div style={S.loading}>Loading contacts…</div>
          ) : contacts.length === 0 ? (
            <div style={S.empty}>No contacts yet. Add your first CO, teaming partner, or sub.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {grouped.map(g => (
                <div key={g.meta.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 4, height: 18, background: g.meta.color, borderRadius: 2 }} />
                    <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>{g.meta.icon} {g.meta.label} ({g.items.length})</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {g.items.map(c => (
                      <div key={c.id} onClick={() => openContact(c)} style={{
                        ...S.listRow,
                        borderLeft: `3px solid ${g.meta.color}`,
                        background: selected?.id === c.id ? GOV_COLORS.cardHi : GOV_COLORS.card,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                            {c.full_name}
                            {c.do_not_contact && <span style={{ ...S.chip, background: GOV_COLORS.danger + '22', color: GOV_COLORS.danger, marginLeft: 8, fontSize: 10 }}>DNC</span>}
                          </div>
                          <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 2 }}>
                            {c.title && <span>{c.title} · </span>}
                            {c.organization || '—'}
                          </div>
                        </div>
                        <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, textAlign: 'right' }}>
                          {c.email && <div>{c.email}</div>}
                          {c.phone && <div>{c.phone}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div style={{ ...S.card, alignSelf: 'flex-start', position: 'sticky', top: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{selected.full_name}</div>
                <div style={{ ...S.chip, background: typeMeta(selected.contact_type).color + '22', color: typeMeta(selected.contact_type).color, marginTop: 6 }}>
                  {typeMeta(selected.contact_type).icon} {typeMeta(selected.contact_type).label}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: GOV_COLORS.textMuted, cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>

            {selected.title && <div style={{ color: GOV_COLORS.textMuted, fontSize: 13, marginBottom: 10 }}>{selected.title}</div>}
            {selected.organization && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Organization</div>
                <div style={S.kvValue}>{selected.organization}{selected.sub_organization ? ` · ${selected.sub_organization}` : ''}</div>
              </div>
            )}
            {selected.email && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Email</div>
                <a href={`mailto:${selected.email}`} style={{ color: GOV_COLORS.accent, fontWeight: 600 }}>{selected.email}</a>
              </div>
            )}
            {selected.phone && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Phone</div>
                <a href={`tel:${selected.phone}`} style={{ color: GOV_COLORS.accent, fontWeight: 600 }}>{selected.phone}</a>
              </div>
            )}
            {(selected.city || selected.state) && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Location</div>
                <div style={S.kvValue}>{[selected.city, selected.state, selected.zip].filter(Boolean).join(', ')}</div>
              </div>
            )}
            {selected.tags && selected.tags.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {selected.tags.map(t => (
                    <span key={t} style={{ ...S.chip, background: GOV_COLORS.accent + '22', color: GOV_COLORS.accent }}>{t}</span>
                  ))}
                </div>
              </div>
            )}
            {selected.notes && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Notes</div>
                <div style={{ ...S.kvValue, fontSize: 13, whiteSpace: 'pre-wrap' }}>{selected.notes}</div>
              </div>
            )}

            {linkedSols.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={S.kvLabel}>Linked Solicitations ({linkedSols.length})</div>
                {linkedSols.map(l => (
                  <a key={l.solicitation.id} href={`/gov/solicitations/${l.solicitation.id}`} style={{
                    display: 'block', padding: '8px 10px', background: '#0F1923', borderRadius: 6,
                    marginTop: 6, textDecoration: 'none', color: GOV_COLORS.text, fontSize: 12,
                  }}>
                    <div style={{ fontWeight: 700 }}>{l.solicitation.solicitation_number || '—'}</div>
                    <div style={{ color: GOV_COLORS.textMuted }}>{l.solicitation.title}</div>
                    {l.role_on_solicitation && <div style={{ color: GOV_COLORS.accent, marginTop: 2 }}>{l.role_on_solicitation}</div>}
                  </a>
                ))}
              </div>
            )}

            {linkedJobs.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={S.kvLabel}>Linked Construction Jobs ({linkedJobs.length})</div>
                {linkedJobs.map(l => (
                  <a key={l.construction_job.id} href={`/gov/construction/${l.construction_job.id}`} style={{
                    display: 'block', padding: '8px 10px', background: '#0F1923', borderRadius: 6,
                    marginTop: 6, textDecoration: 'none', color: GOV_COLORS.text, fontSize: 12,
                  }}>
                    <div style={{ fontWeight: 700 }}>{l.construction_job.contract_number || l.construction_job.bid_number || '—'}</div>
                    <div style={{ color: GOV_COLORS.textMuted }}>{l.construction_job.solicitation?.title || '—'}</div>
                    {l.role_on_job && <div style={{ color: GOV_COLORS.accent, marginTop: 2 }}>{l.role_on_job}</div>}
                  </a>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={openEditModal} style={{ ...S.addBtn, flex: 1 }}>Edit</button>
              <button onClick={deleteContact} style={S.dangerBtn}>Delete</button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div style={S.modalBackdrop} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>{(form as any).id ? 'Edit Contact' : 'New Contact'}</h2>

            <div style={S.modalRow}>
              <label style={S.modalLabel}>Full Name *</label>
              <input type="text" value={form.full_name || ''} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                style={{ ...S.input, width: '100%' }} placeholder="Jordan Carlsen" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Type *</label>
                <select value={form.contact_type || 'poc'} onChange={e => setForm(p => ({ ...p, contact_type: e.target.value }))} style={{ ...S.select, width: '100%' }}>
                  {CONTACT_TYPES.map(ct => <option key={ct.key} value={ct.key}>{ct.icon} {ct.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.modalLabel}>Title</label>
                <input type="text" value={form.title || ''} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  style={{ ...S.input, width: '100%' }} placeholder="Contracting Officer" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Organization</label>
                <input type="text" value={form.organization || ''} onChange={e => setForm(p => ({ ...p, organization: e.target.value }))}
                  style={{ ...S.input, width: '100%' }} placeholder="USDA Forest Service" />
              </div>
              <div>
                <label style={S.modalLabel}>Sub-Org / Division</label>
                <input type="text" value={form.sub_organization || ''} onChange={e => setForm(p => ({ ...p, sub_organization: e.target.value }))}
                  style={{ ...S.input, width: '100%' }} placeholder="Region 2" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Email</label>
                <input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>Phone</label>
                <input type="tel" value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={{ ...S.input, width: '100%' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>City</label>
                <input type="text" value={form.city || ''} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>State</label>
                <input type="text" value={form.state || ''} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>Zip</label>
                <input type="text" value={form.zip || ''} onChange={e => setForm(p => ({ ...p, zip: e.target.value }))} style={{ ...S.input, width: '100%' }} />
              </div>
            </div>

            <div style={S.modalRow}>
              <label style={S.modalLabel}>Tags (comma-separated)</label>
              <input type="text" value={(form.tags || []).join(', ')} onChange={e => setForm(p => ({ ...p, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                style={{ ...S.input, width: '100%' }} placeholder="roofing, region-2, priority" />
            </div>

            <div style={S.modalRow}>
              <label style={S.modalLabel}>Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3}
                style={{ ...S.input, width: '100%', resize: 'vertical' as const, minHeight: 70 }} placeholder="Prefers email · met at 8(a) matchmaker Q1 2026" />
            </div>

            <div style={S.modalFooter}>
              <button onClick={() => setShowModal(false)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={saveContact} disabled={saving} style={{ ...S.addBtn, flex: 2, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : ((form as any).id ? 'Save Contact' : 'Create Contact')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
